import { useCallback } from 'react'
import { gql, type Reference, type StoreObject } from '@apollo/client'
import { toast } from '@buffer-mono/popcorn'

import type { CommentData } from '~publish/gql/graphql'
import { useTypedMutation } from '~publish/hooks/useTypedMutation'
import { useCommentStatus } from '../CommentsFilters/useFilters'

const ReplyToCommentGQL = gql`
  mutation ReplyToComment($input: ReplyToCommentInput!) {
    replyToComment(input: $input) {
      __typename
      ... on ReplyToCommentSuccess {
        comment {
          ... on BaseComment {
            id
            text
            publishedAt
            status
            parentId
            author {
              username
              avatar
              __typename
            }
            assets {
              id
              source
              thumbnail
              type
              mimeType
            }
          }
          ... on CommentFromBuffer {
            id
            text
            author {
              username
              avatar
            }
            publishedAt
            status
            assets {
              id
              type
              source
              thumbnail
            }
          }
          ... on CommentFromThirdParty {
            id
            text
            author {
              username
              avatar
            }
            publishedAt
            status
            assets {
              id
              type
              source
              thumbnail
            }
          }
        }
      }
      ... on NotFoundError {
        message
      }
      ... on UnauthorizedError {
        message
      }
      ... on UnexpectedError {
        message
      }
    }
  }
`

type UseReplyToCommentReturn = {
  replyToComment: (
    comment: CommentData,
    text: string,
  ) => Promise<{
    success: boolean
    data?: CommentData
    error?: string
    errorType?: string
  }>
  loading: boolean
  error?: Error | null
}

type ReplyToCommentData = {
  replyToComment:
    | {
        __typename: 'ReplyToCommentSuccess'
        comment?: CommentData
      }
    | {
        __typename: 'NotFoundError'
        message: string
      }
    | {
        __typename: 'UnauthorizedError'
        message: string
      }
    | {
        __typename: 'UnexpectedError'
        message: string
      }
}

/**
 * Context type for cache updates containing the original comment
 */
type UpdateContext = {
  /** The original comment that was replied to */
  originalComment: CommentData
}

/**
 * Hook for replying to comments with GraphQL mutation and cache management
 *
 * This hook handles:
 * - Executing the ReplyToComment GraphQL mutation
 * - Updating Apollo cache with the new reply
 * - Changing comment status from 'unreplied' to 'replied'
 * - Removing replied comments from 'unreplied' filtered views
 * - Providing proper error handling and type safety
 *
 * The reply flow:
 * 1. User submits reply → mutation executes
 * 2. Cache updates immediately with new reply data
 * 3. Comment status changes to 'replied'
 * 4. Comment removed from 'unreplied' filtered views
 * 5. Success/error state returned to caller
 *
 * @returns Object containing replyToComment function, loading state, and error
 *
 * @example
 * ```typescript
 * const { replyToComment, loading, error } = useReplyToComment()
 *
 * const handleReply = async (comment, text) => {
 *   const result = await replyToComment(comment, text)
 *   if (result.success) {
 *     // Handle success
 *   } else {
 *     // Handle error
 *   }
 * }
 * ```
 */
export const useReplyToComment = (): UseReplyToCommentReturn => {
  const status = useCommentStatus()

  const [mutate, { loading, error }] = useTypedMutation(
    ReplyToCommentGQL,
    (data: ReplyToCommentData) => data.replyToComment,
    {
      successTypename: 'ReplyToCommentSuccess',
      update: (
        cache,
        { data },
        options: {
          context?: UpdateContext
        },
      ): void => {
        const result = data?.replyToComment
        if (!result || result.__typename !== 'ReplyToCommentSuccess') {
          return
        }

        const newReply = result.comment
        if (!newReply) {
          return
        }

        // Get the parent comment from the context
        let originalComment
        if (
          options?.context &&
          'originalComment' in options.context &&
          options.context.originalComment
        ) {
          originalComment = options.context.originalComment
        } else {
          return
        }

        const lastReply = {
          id: newReply.id,
          text: newReply.text,
          organizationId: originalComment.organizationId,
          serviceType: originalComment.serviceType,
          publishedAt: newReply.publishedAt,
          parentId: newReply.parentId,
          status: newReply.status,
          isOwnReply: true,
          author: {
            username: newReply.author.username,
            name: newReply.author.username,
            avatar: newReply.author.avatar,
          },
          assets: newReply.assets || [],
        }

        // 1. Update the comment in the cache with the new status and lastReply
        cache.modify({
          id: cache.identify({
            __typename: originalComment.__typename,
            id: originalComment.id,
          }),
          fields: {
            status: () => 'replied',
            lastReply: () => lastReply,
          },
        })

        // 2. Decide if the comment should be removed from the current view
        const shouldRemoveFromList = status === 'unreplied'

        if (shouldRemoveFromList) {
          // Find all comments queries in the cache and update them
          cache.modify({
            fields: {
              comments: (
                existingCommentsRef: Reference | StoreObject,
                { readField, storeFieldName },
              ) => {
                const match = storeFieldName.match(/"status":\["(.*?)"\]/)
                const statusInStore = match ? match[1] : null

                // Skip if this isn't the current filter's query
                if (statusInStore !== status) {
                  return existingCommentsRef
                }

                const edges =
                  readField<{ node: CommentData }[]>(
                    'edges',
                    existingCommentsRef,
                  ) || []

                const newEdges = edges.filter((edge: { node: CommentData }) => {
                  const commentId = readField('id', readField('node', edge))
                  return commentId !== originalComment.id
                })

                return {
                  ...existingCommentsRef,
                  edges: newEdges,
                }
              },
            },
          })
        }
      },
    },
  )

  const replyToComment = useCallback(
    async (
      comment: CommentData,
      text: string,
    ): Promise<{
      success: boolean
      data?: CommentData
      error?: string
      errorType?: string
    }> => {
      try {
        const result = await mutate({
          variables: {
            input: {
              commentId: comment.id,
              reply: {
                text,
              },
            },
          },
          context: {
            originalComment: comment,
          },
        })

        if (!result.success) {
          const errorMessage: string =
            result.error?.message || 'Failed to send reply'
          toast.error('An error occurred while replying to the comment')

          const errorType = result.error?.__typename || 'UnexpectedError'

          // Send event to Clarity to easily track errors in the community
          if (window.clarity) {
            if (errorType === 'NotFoundError') {
              window.clarity(
                'set',
                'community_comment_reply_error_not_found',
                comment.id,
              )
            } else {
              window.clarity(
                'set',
                'community_comment_reply_error_unexpected',
                comment.id,
              )
            }
          }
          return {
            success: false,
            error: errorMessage,
            errorType,
          }
        }

        return { success: true, data: result.data.comment }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error replying to comment:', error)
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to send reply'
        toast.error('An error occurred while replying to the comment')

        // Send event to Clarity to easily track unexpected errors
        if (window.clarity) {
          window.clarity('set', 'community_comment_reply_error', comment.id)
        }

        return {
          success: false,
          error: errorMessage,
          errorType: 'UnexpectedError',
        }
      }
    },
    [mutate],
  )

  return {
    replyToComment,
    loading,
    error: error && 'message' in error ? new Error(error.message) : null,
  }
}
